/**
 * Company card for list view with optional selection checkbox.
 *
 * AC-001: Checkbox for company comparison selection
 */

import { Link } from 'react-router-dom';
import type { CompanyListItem } from '../../types';
import { KBStatusBadge } from './KBStatusBadge';
import { Checkbox } from '../ui/checkbox';

interface CompanyCardProps {
  company: CompanyListItem;
  isSelected?: boolean;
  onToggleSelect?: (companyId: string) => void;
  selectionDisabled?: boolean;
}

export function CompanyCard({
  company,
  isSelected = false,
  onToggleSelect,
  selectionDisabled = false,
}: CompanyCardProps) {
  const companyTypeLabels: Record<string, string> = {
    public: 'Public',
    pe_backed: 'PE-Backed',
    private: 'Private',
    subsidiary: 'Subsidiary',
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onToggleSelect && !selectionDisabled) {
      onToggleSelect(company.id);
    }
  };

  return (
    <div
      className={`relative rounded-lg border bg-white shadow-sm transition-shadow hover:shadow-md ${
        isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
      }`}
    >
      {/* Selection checkbox */}
      {onToggleSelect && (
        <div
          className="absolute top-3 left-3 z-10"
          onClick={handleCheckboxClick}
          data-testid={`company-checkbox-${company.id}`}
        >
          <Checkbox
            checked={isSelected}
            disabled={selectionDisabled}
            aria-label={`Select ${company.name} for comparison`}
          />
        </div>
      )}

      <Link to={`/companies/${company.id}`} className="block p-6">
        <div className={`flex justify-between items-start ${onToggleSelect ? 'pl-8' : ''}`}>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{company.name}</h3>
            <div className="mt-1 flex items-center space-x-2">
              {company.ticker && (
                <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                  {company.ticker}
                </span>
              )}
              <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                {companyTypeLabels[company.company_type] || company.company_type}
              </span>
            </div>
          </div>
          <KBStatusBadge
            completeness={company.kb_completeness}
            isBuilding={company.kb_is_building}
          />
        </div>
        <p className="mt-3 text-sm text-gray-500">{company.domain}</p>
      </Link>
    </div>
  );
}

export default CompanyCard;
